// Audio (docs/platform/ClientShell.md "Audio", docs/platform/UiFidelity.md §3 "Audio mix"): plays the
// converted samples (Ogg/Opus) for the sim's sound events (`sim::view::events`: every
// start/stop/fade/pause/resume of an FMOD event instance of the sim's audio model, its parameter
// values and `StopAutoPlaySound`'s muting) as FMOD Ex's event system plays the FEV catalogue
// (audio/events.json). The rules below were measured with the game's libfmodevent (UiFidelity.md
// §3):
// - The parameter (an event has at most one) starts at its minimum on a new handle and keeps its
//   value on the same handle, across stops and restarts. While the event plays, `velocity`
//   (range fractions per second) advances it, holding at the maximum (flags 0x2, catalogue
//   "oneshot") or wrapping (flags 0x8, catalogue "oneshot_stop_event"). A set clamps to the
//   range; with `seek_speed` (range fractions per second) a playing event moves there at that
//   speed with its velocity suspended, otherwise the value jumps.
// - A layer's sound instance plays while the normalised parameter lies in [start, start +
//   length]. Entering starts it; leaving stops a looping one and mutes a one-shot one, which plays
//   on. `fade_in`/`fade_out` (fractions of the length) crossfade it: gain sqrt(f) for type 2, f for
//   type 6. A sound definition with a spawn time starts an entry after its trigger delay and then
//   one every spawn time, at most `max_spawned` at once.
// - The envelopes run over the normalised parameter. A segment takes the shape of its end point:
//   smooth 3t²−2t³ or linear. Volume is gain y, pitch is 8(y − 0.5) octaves, pan is 1 − 2y. DSP
//   parameters use mapping 0 (linear over the DSP parameter's range) or 1 (min·(max/min)^y).
//   Volume and pan act on each channel before the layer's DSP chain, which runs in envelope order.
// - The mix: the category volume chain and its playback limit (Fanfares and Frontend_Extra: one,
//   the oldest is stolen), the event volume, fade in and out, pitch and its randomisation, the
//   sound definition's volume and pitch with their randomisation, the layer sound's volume, and
//   FMOD Ex's 3D attenuation and pan for `3d` events. The listener sits where
//   `CameraMan::UpdateGraphicCameras` 0xb61280 puts it: at the camera's look-at point.
// The sim's model owns the events' instance pools (max_playbacks): every start it reports has
// already got an instance. Playback never feeds back into the sim (ARCHITECTURE "Audio lengths
// are logic input").
import { assetJson, assetUrl } from '../assets.ts';
import {
  AUDIO_ALL_TYPES, AUDIO_CATEGORY_PAUSE, AUDIO_CATEGORY_VOLUME, AUDIO_TRACK_FADE_OUT, AUDIO_TRACK_LOAD, AUDIO_TRACK_PLAY,
  AUDIO_TRACK_POSITION, AUDIO_TRACK_QUEUE, SOUND_AUDIO, SOUND_FADE, SOUND_MUTE, SOUND_PARAM, SOUND_PAUSE, SOUND_POS, SOUND_RESUME,
  SOUND_START, SOUND_STOP, SOUND_TEAM, SOUND_VOLUME, type SoundEvent,
} from '../sim/view.ts';

type Entry = { file: string; weight: number; status: string; loop_start: number; loop_end: number; rate: number };
type SoundDefProps = {
  volume?: number; volume_randomization_min_gain?: number; pitch_octaves?: number; pitch_randomization_octaves?: number;
  spawn_time_min_ms?: number; spawn_time_max_ms?: number; trigger_delay_min_ms?: number; trigger_delay_max_ms?: number; max_spawned?: number;
};
type SoundDef = { index: number; entries: Entry[]; properties?: SoundDefProps };
type LayerSound = {
  sounddef: number; loop_mode: string; start_mode: string; volume: number; start: number; length: number;
  fade_in: number; fade_out: number; fade_in_type: number; fade_out_type: number;
};
type Point = [number, number, string];
type Envelope = { kind: string; dsp: string | null; dsp_param_index: number; dsp_parent_envelope: number; mapping: number; points: Point[] };
type Layer = { sounds: LayerSound[]; control_parameter: string | null; envelopes?: Envelope[] };
type Param = { name: string; min: number; max: number; velocity: number; seek_speed: number; loop_mode: string; primary: boolean };
type EventProps = {
  volume?: number; fade_in_ms?: number; fade_out_ms?: number;
  pitch_octaves?: number; pitch_randomization_octaves?: number;
  mode?: string; min_distance?: number; max_distance?: number; rolloff?: string; pan_level_3d?: number; max_playbacks?: number;
};
type EventDef = { path: string; category_path?: string; layers: Layer[]; parameters?: Param[]; properties?: EventProps };
type Category = { path: string; volume: number; children: string[]; max_playbacks?: number; max_playbacks_behavior?: string };
type Catalogue = { events: EventDef[]; sounddefs: SoundDef[]; categories: Category[] };

/** `eAudioCategory` → the FEV category `XAudioManager::GetFMODCategory` 0x11c7020 finds by name
 * (`EventSystem::getCategory`: the top-level ones; null: `Ambience` and `Frontend_Ambience` sit
 * under SFX and are not found, so the game never sets them). */
const CATEGORY_PATHS: (string | null)[] = [
  '/master', '/master/SFX', '/master/music', null, '/master/Speech', '/master/Frontend', '/master/Frontend_Music', null,
  '/master/Frontend_Extra', '/master/Fanfares',
];
const CAT_SFX = 1, CAT_MUSIC = 2, CAT_AMBIENCE = 3, CAT_SPEECH = 4, CAT_FRONTEND_MUSIC = 6, CAT_FRONTEND_AMBIENCE = 7, CAT_FRONTEND_EXTRA = 8;
/** `XAudioManager::SetCategoryVolume` 0x11c71f0 drops a change of at most this (DAT_0159bd48). */
const CATEGORY_EPSILON = Math.fround(0.01);
/** A new profile's levels (CommonData/InitSave.txt: SoundVolume, MusicVolume, NarrationVolume). */
export const PROFILE = { sfx: 1, music: 0.6, narration: 1 };

/** Time constant of parameter changes (s): FMOD applies a value at its next update and ramps the
 * channel volume over the mix block. */
const SMOOTH = 0.01;
/** A biquad's Q (dB) without resonance (Butterworth, 1/√2): the "Simple" filters. */
const NO_RESONANCE_DB = 20 * Math.log10(Math.SQRT1_2);

/** FMOD Ex's DSP parameter ranges in parameter order (`FMOD_DSP_GetParameterInfo`, oracle). */
const DSP_RANGES: Record<string, [number, number][]> = {
  'FMOD Lowpass Simple': [[10, 22000]],
  'FMOD Highpass Simple': [[10, 22000]],
  'FMOD Lowpass': [[10, 22000], [1, 10]],
  'FMOD Compressor': [[-60, 0], [10, 200], [20, 1000], [0, 30]],
  'FMOD Distortion': [[0, 1]],
  'FMOD Tremolo': [[0.01, 20], [0, 1], [0, 1], [-1, 1], [0, 1], [0, 1], [0, 1], [-1, 1]],
  'SFX Reverb': [[-10000, 0], [-10000, 0], [-10000, 0], [0.1, 20], [0.1, 2], [-10000, 1000], [0, 0.3], [-10000, 2000], [0, 0.1],
    [0, 100], [0, 100], [20, 20000], [-10000, 0], [20, 1000]],
};

/** One FMOD DSP unit as Web Audio nodes (`input` .. `output`); `apply` sets its parameters. */
type DspUnit = { input: AudioNode; output: AudioNode; apply: (p: number[], t: number) => void; stop?: () => void };

function makeDsp(ctx: AudioContext, name: string): DspUnit | null {
  switch (name) {
    case 'FMOD Lowpass Simple': case 'FMOD Highpass Simple': case 'FMOD Lowpass': {
      // Simple: no resonance; FMOD Lowpass: resonance = the filter's Q.
      const f = ctx.createBiquadFilter();
      f.type = name === 'FMOD Highpass Simple' ? 'highpass' : 'lowpass';
      return {
        input: f, output: f, apply: (p, t) => {
          f.frequency.setTargetAtTime(Math.min(p[0], ctx.sampleRate / 2), t, SMOOTH);
          f.Q.setTargetAtTime(name === 'FMOD Lowpass' ? 20 * Math.log10(Math.max(1, p[1])) : NO_RESONANCE_DB, t, SMOOTH);
        },
      };
    }
    case 'FMOD Compressor': {
      // A limiter at the threshold (FMOD Ex: "make-up gain applied after limiting"), then the gain.
      const c = ctx.createDynamicsCompressor(), g = ctx.createGain();
      c.knee.value = 0;
      c.ratio.value = 20;
      c.connect(g);
      return {
        input: c, output: g, apply: (p, t) => {
          c.threshold.setValueAtTime(p[0], t);
          c.attack.setValueAtTime(p[1] / 1000, t);
          c.release.setValueAtTime(p[2] / 1000, t);
          g.gain.setTargetAtTime(10 ** (p[3] / 20), t, SMOOTH);
        },
      };
    }
    case 'FMOD Distortion': {
      // Soft clipping (1 + k)x / (1 + k|x|), k = 2·level / (1 − level).
      const w = ctx.createWaveShaper();
      let level = -1;
      return {
        input: w, output: w, apply: p => {
          if (p[0] === level) return;
          level = p[0];
          const k = 2 * level / Math.max(1e-6, 1 - level), n = 1025, curve = new Float32Array(n);
          for (let i = 0; i < n; i++) {
            const x = i / (n - 1) * 2 - 1;
            curve[i] = (1 + k) * x / (1 + k * Math.abs(x));
          }
          w.curve = curve;
        },
      };
    }
    case 'FMOD Tremolo': {
      // Gain 1 − depth·(1 − lfo)/2 with a triangle LFO (shape 0); every use keeps skew 0, duty
      // 0.5, square 0, phase 0 and spread 0.
      const g = ctx.createGain(), lfo = ctx.createOscillator(), depth = ctx.createGain();
      lfo.type = 'triangle';
      lfo.connect(depth).connect(g.gain);
      lfo.start();
      return {
        input: g, output: g, stop: () => lfo.stop(), apply: (p, t) => {
          lfo.frequency.setTargetAtTime(p[0], t, SMOOTH);
          g.gain.setTargetAtTime(1 - p[1] / 2, t, SMOOTH);
          depth.gain.setTargetAtTime(p[1] / 2, t, SMOOTH);
        },
      };
    }
    case 'SFX Reverb': {
      // The dry level; the reverb's own level (Room + Reverb) is at most −49 dB in every use.
      const g = ctx.createGain();
      return { input: g, output: g, apply: (p, t) => g.gain.setTargetAtTime(10 ** (p[0] / 2000), t, SMOOTH) };
    }
  }
  return null;
}

/** An envelope's value at normalised parameter `x`: end values hold outside the points; a segment
 * uses its end point's shape (smooth 3t²−2t³, linear). */
function envelope(points: Point[], x: number): number {
  if (!points.length) return 0;
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1], [x1, y1, shape] = points[i];
    if (x <= x1) {
      const t = x1 > x0 ? (x - x0) / (x1 - x0) : 0;
      return y0 + (y1 - y0) * (shape === 'linear' ? t : t * t * (3 - 2 * t));
    }
  }
  return points[points.length - 1][1];
}

const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

/** `SoundResourceMan::SoundResourceType` (kMusic, kAmbient, kWater) and track indices. */
export const TRACK_MUSIC = 0, TRACK_AMBIENT = 1, TRACK_WATER = 2, TRACK_MAIN = 0, TRACK_SUB = 1;

/** A parameter change: smoothed (FMOD ramps a channel's volume across the mix block), or a jump
 * for a new playback and a sound that starts (FMOD starts a channel at its values). */
function ramp(a: AudioParam, v: number, t: number, jump: boolean): void {
  if (jump) {
    a.cancelScheduledValues(t);
    a.setValueAtTime(v, t);
  } else a.setTargetAtTime(v, t, SMOOTH);
}

/** A source of a layer's sound instance, with what a pause needs to start it again: its buffer,
 * loop, start time and the buffer offset it started at. */
type Src = { src: AudioBufferSourceNode | null; oct: number; buf: AudioBuffer; g: GainNode; loop: [number, number] | null; t0: number; off: number };

/** A layer's sound instance: its sources (with their own pitch in octaves) behind a gain that is
 * the sound's volume times its crossfade (0 outside its region). */
type Voice = {
  s: LayerSound; gain: GainNode; srcs: Src[];
  /** The cursor is inside the region. */
  inside: boolean;
  /** Triggers waiting for their buffer or trigger delay. */
  pending: number;
  /** The next spawn (AudioContext time; Infinity: none). */
  next: number;
};
type LayerState = {
  layer: Layer; vol: GainNode; pan: StereoPannerNode; dsps: { unit: DspUnit; envs: Envelope[]; ranges: [number, number][] }[];
  /** The pitch envelope in octaves. */
  pitch: number; voices: Voice[];
};

/** A playing instance: its event volume (`gain`: the designed volume, `Event::setPropertyByIndex
 * (VOLUME)` replaces it), then the event fade in and out (`fade`); a 3D event's distance gain
 * and stereo pan sit between those and its category (`spatial`), set from `pos` and the
 * listener. `paused` (`Event::setPaused`) and `catPaused` (its category's `setPaused`) freeze it:
 * FMOD's paused channels neither sound nor advance. */
type Playing = {
  key: string; serial: number; eventIdx: number; ev: EventDef; props: EventProps; category: string;
  gain: GainNode; fade: GainNode; volume: number; spatial: { dist: GainNode; pan: StereoPannerNode } | null; pos: [number, number, number] | null;
  param: Param | null; value: number; target: number | null; last: number;
  layers: LayerState[]; octaves: number; paused: boolean; catPaused: boolean; frozenAt: number; muted: boolean; stopped: boolean;
  triggered: boolean; order: number;
  stops: (() => void)[];
  /** Not refreshed yet: the first refresh sets the layers' values at once. */
  fresh: boolean;
};

/** An instance the client makes itself (`SoundResourceMan` tracks, the HUD's and results' UI
 * sounds): `getEvent` → a handle of its own key. */
export type LocalSound = { key: string; serial: number; path: string; ev: EventDef; volume: number | null; pos: [number, number, number] | null };

/** `SoundResourceMan::SoundTrack` (types.jsonl:8073): one music, ambient or water track. */
type Track = {
  name: string; queued: string; inst: LocalSound | null; origin: number; current: number; speed: number;
  destroy: boolean; fadingOut: boolean; fadingIn: boolean;
};

export class Audio {
  ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private events = new Map<string, EventDef>();
  /** FMOD's lookup ignores case. */
  private eventsLower = new Map<string, EventDef>();
  private sounddefs: SoundDef[] = [];
  private categories: Category[] = [];
  /** One gain node per FEV category, chained to its parent's. */
  private categoryGain = new Map<string, GainNode>();
  /** `XAudioManager::m_fCategoryVolume[10]`: 1.0 after `LoadEventFile`, then the profile's. */
  private catLevel = new Array<number>(10).fill(1);
  /** Paused FEV categories (their paths). */
  private pausedCats = new Set<string>();
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private playing = new Map<string, Playing>();
  /** The round results' AutoPlaySound events (parade, fanfare, crowd) `FadeoutResultsMusic` mutes. */
  private resultsSounds: string[] = [];
  /** The end of a fade-out per key: FMOD plays a fading instance until its fade is over. */
  private fadeEnd = new Map<string, number>();
  /** The instances fading out (until their `fadeEnd`). */
  private fading = new Map<string, Playing>();
  /** The parameter value of each instance handle that is not playing (FMOD keeps it on the
   * handle; a new handle starts at the minimum). */
  private params = new Map<string, { serial: number; value: number }>();
  /** The entry each random-no-repeat sound definition played last. */
  private lastEntry = new Map<number, Entry>();
  private loaded: Promise<void> | null = null;
  private starts = 0;
  private localN = 0;
  /** The UI sounds' instances per event (the event's `max_playbacks`, oldest stolen). */
  private uiSounds = new Map<string, LocalSound[]>();
  /** `SoundResourceMan::m_SoundResources[3][2]`. */
  private tracks: Track[][] = [0, 1, 2].map(() => [0, 1].map(() => Audio.emptyTrack()));
  /** The track types whose position follows the camera's x (`AUDIO_TRACK_POSITION`: y, z). */
  private follow = new Map<number, [number, number]>();
  /** The camera's x (`CameraMan::GetCameraPosition`). */
  private eyeX = 0;
  /** The last track update (AudioContext time). */
  private tracksAt = 0;
  /** The music a `TurnLogic::PostRestoreSnapshot` queue checks the music main track against. */
  private restoreMusic = '';
  /** Counts of started events (tests). */
  started = 0;
  /** Whether a press refusal of `team` plays here (null: every team, offline). */
  localTeam: ((team: number) => boolean) | null = null;
  /** The FMOD listener: position and unit right axis (`listen`). */
  private ear = { x: 0, y: 0, z: 0, rx: 1, ry: 0, rz: 0 };

  /** Loads the catalogue (once) and applies a new profile's levels (`SetSoundLevels`). */
  load(): Promise<void> {
    this.loaded ??= assetJson<Catalogue>('audio/events.json').then(c => {
      for (const e of c.events) {
        this.events.set(e.path, e);
        this.eventsLower.set(e.path.toLowerCase(), e);
      }
      this.sounddefs = c.sounddefs;
      this.categories = c.categories ?? [];
      this.setSoundLevels(PROFILE.music, PROFILE.sfx, PROFILE.narration);
    });
    return this.loaded;
  }

  /** Creates the AudioContext; browsers allow it only after a user gesture. A host without an
   * audio device (headless test browsers) rejects the resume: the match plays silently. Tracks
   * that were to play before start now. */
  unlock(): void {
    if (this.ctx) { this.ctx.resume().catch(e => console.warn('audio', e)); return; }
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.catLevel[0];
    this.master.connect(this.ctx.destination);
    this.tracksAt = this.ctx.currentTime;
    for (const row of this.tracks) for (const t of row) if (t.inst && t.inst.serial > 0 && !t.destroy) this.localPlay(t.inst);
  }

  private event(path: string): EventDef | undefined {
    return this.events.get(path) ?? this.eventsLower.get(path.toLowerCase());
  }

  private buffer(file: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(file);
    if (!p) {
      const ctx = this.ctx;
      p = (async () => {
        if (!ctx) return null;
        const r = await fetch(assetUrl(file));
        if (!r.ok) return null;
        return ctx.decodeAudioData(await r.arrayBuffer());
      })().catch(() => null);
      this.buffers.set(file, p);
    }
    return p;
  }

  /** An entry of `def` by weight; random-no-repeat (every Worms definition) never repeats the
   * previous entry, so two entries alternate. */
  private pick(def: SoundDef): Entry | null {
    const last = this.lastEntry.get(def.index);
    let ok = def.entries.filter(e => e.status === 'ok');
    if (ok.length > 1 && last) ok = ok.filter(e => e !== last);
    let total = 0;
    for (const e of ok) total += Math.max(1, e.weight);
    let r = Math.random() * total, pick: Entry | null = ok[0] ?? null;
    for (const e of ok) { r -= Math.max(1, e.weight); if (r <= 0) { pick = e; break; } }
    if (pick) this.lastEntry.set(def.index, pick);
    return pick;
  }

  /** The level of FEV category `path` as the game runs it: `EventCategory::setVolume` replaces
   * the FEV's designed volume, and the game sets every category it finds (harness/audio
   * fmodprobe.py: Speech's -4 dB becomes 0 dB); the others keep the designed one. */
  private categoryLevel(path: string): number {
    const i = CATEGORY_PATHS.indexOf(path);
    return i >= 0 ? this.catLevel[i] : this.categories.find(c => c.path === path)?.volume ?? 1;
  }

  /** The gain node of FEV category `path` (FMOD EventCategory volumes multiply down the tree). */
  private category(path: string | undefined): GainNode | null {
    const ctx = this.ctx, master = this.master;
    if (!ctx || !master) return null;
    if (!path || path === '/master') return master;
    let g = this.categoryGain.get(path);
    if (!g) {
      g = ctx.createGain();
      g.gain.value = this.categoryLevel(path);
      g.connect(this.category(path.slice(0, path.lastIndexOf('/'))) ?? master);
      this.categoryGain.set(path, g);
    }
    return g;
  }

  /** `XAudioManager::SetCategoryVolume(eCategory, v)` 0x11c71f0: nothing for a change of at most
   * 0.01; the FMOD category (if `getCategory` finds it) gets `v`, the stored level always. */
  setCategoryVolume(cat: number, v: number): void {
    if (Math.abs(Math.fround(this.catLevel[cat] - v)) <= CATEGORY_EPSILON) return;
    this.catLevel[cat] = v;
    const path = CATEGORY_PATHS[cat];
    const g = path === '/master' ? this.master : path ? this.categoryGain.get(path) : null;
    if (g && this.ctx) g.gain.setValueAtTime(v, this.ctx.currentTime);
  }

  /** `SoundResourceMan::SetSoundLevels(music, sfx, narration, master -1)` 0xbc6bb0: the music
   * categories (Music, Ambience, Frontend_Music, Frontend_Ambience) get `music`, Speech and
   * Frontend_Extra `narration`, the rest `sfx`. */
  setSoundLevels(music: number, sfx: number, narration: number): void {
    for (let cat = 1; cat < 10; cat++) {
      const v = cat === CAT_MUSIC || cat === CAT_AMBIENCE || cat === CAT_FRONTEND_MUSIC || cat === CAT_FRONTEND_AMBIENCE ? music
        : cat === CAT_SPEECH || cat === CAT_FRONTEND_EXTRA ? narration : sfx;
      this.setCategoryVolume(cat, v);
    }
  }

  /** `XAudioManager::Pause`/`Resume(eCategory)`: `EventCategory::setPaused` freezes every event
   * of the category and its subcategories. */
  pauseCategory(cat: number, paused: boolean): void {
    const path = CATEGORY_PATHS[cat];
    if (!path) return;
    if (paused) this.pausedCats.add(path); else this.pausedCats.delete(path);
    for (const p of this.playing.values()) this.setFrozen(p, p.paused, this.catIsPaused(p.category));
  }

  private catIsPaused(path: string): boolean {
    for (let q = path; q; q = q.slice(0, q.lastIndexOf('/'))) if (this.pausedCats.has(q)) return true;
    return false;
  }

  /** The parameter `name` selects: FMOD's case-insensitive `Event::getParameter` (`byName`), or
   * `XSoundInstance::GetParameterIndex`'s exact name with the primary parameter
   * (`getParameterByIndex(-1)`) when none matches. */
  private paramOf(ev: EventDef, name: string, byName: boolean): Param | null {
    const ps = ev.parameters ?? [];
    if (byName) return ps.find(p => p.name.toLowerCase() === name.toLowerCase()) ?? null;
    return ps.find(p => p.name === name) ?? ps.find(p => p.primary) ?? null;
  }

  private setParam(key: string, e: SoundEvent): void {
    const ev = this.event(e.path), q = e.param;
    const param = ev && q ? this.paramOf(ev, q.name, q.byName) : null;
    if (!param || !q) return;
    const value = Math.min(param.max, Math.max(param.min, q.value));
    const p = this.playing.get(key);
    if (p && p.serial === e.serial) {
      if (param.seek_speed > 0 && !p.paused) p.target = value;
      else { p.value = value; p.target = null; }
    } else this.params.set(key, { serial: e.serial, value });
  }

  private start(key: string, e: SoundEvent, ev: EventDef): Playing | null {
    const ctx = this.ctx, out = this.category(ev.category_path);
    if (!ctx || !out) return null;
    this.stop(key, 0);
    this.fadeEnd.delete(key);
    // The category's playback limit (FMOD EventCategory MAX_PLAYBACKS).
    const cat = this.categories.find(c => c.path === ev.category_path);
    const max = cat?.max_playbacks ?? 0;
    if (max > 0) {
      const same = [...this.playing.values()].filter(p => p.category === cat?.path).sort((a, b) => a.order - b.order);
      if (same.length >= max) {
        if (cat?.max_playbacks_behavior?.startsWith('just_fail')) return null;
        this.stop(same[0].key, 0);
      }
    }
    const props = ev.properties ?? {};
    const volume = props.volume ?? 1;
    const gain = ctx.createGain(), fade = ctx.createGain();
    gain.gain.value = volume;
    gain.connect(fade);
    const t = ctx.currentTime, fadeIn = (props.fade_in_ms ?? 0) / 1000;
    if (fadeIn > 0) {
      fade.gain.setValueAtTime(0, t);
      fade.gain.linearRampToValueAtTime(1, t + fadeIn);
    }
    // A 3D event: distance gain and pan after the event gain (FMOD Ex's 3D stage).
    let spatial: Playing['spatial'] = null;
    if (props.mode === '3d') {
      spatial = { dist: ctx.createGain(), pan: ctx.createStereoPanner() };
      fade.connect(spatial.dist).connect(spatial.pan).connect(out);
    } else fade.connect(out);
    const param = ev.parameters?.[0] ?? null;
    const saved = this.params.get(key);
    this.params.delete(key);
    const p: Playing = {
      key, serial: e.serial, eventIdx: e.event, ev, props, category: ev.category_path ?? '', gain, fade, volume, spatial, pos: null,
      // FMOD advances a parameter's velocity by the whole update interval at the event's first
      // update (HomingMissileThrust's Time, ChopperRelease's Thrust in the traces), so the event's
      // clock starts at the previous update.
      param, value: param ? (saved?.serial === e.serial ? saved.value : param.min) : 0, target: null,
      last: t - Math.min(Math.max(0, t - this.tracksAt), 1 / 30), layers: [],
      octaves: (props.pitch_octaves ?? 0) + rand(-1, 1) * (props.pitch_randomization_octaves ?? 0),
      paused: false, catPaused: false, frozenAt: 0, muted: false, stopped: false, triggered: false, order: ++this.starts, stops: [],
      fresh: true,
    };
    for (const layer of ev.layers) {
      // Channel volume and pan, then the layer's DSP chain in envelope order.
      const vol = ctx.createGain(), pan = ctx.createStereoPanner();
      vol.connect(pan);
      const envs = layer.envelopes ?? [];
      const dsps: LayerState['dsps'] = [];
      let tail: AudioNode = pan;
      envs.forEach((en, i) => {
        if (en.kind !== 'dsp' || en.dsp_parent_envelope !== -1 || !en.dsp) return;
        const unit = makeDsp(ctx, en.dsp), ranges = DSP_RANGES[en.dsp];
        if (!unit || !ranges) { console.warn('audio: DSP not modelled', en.dsp, ev.path); return; }
        tail.connect(unit.input);
        tail = unit.output;
        if (unit.stop) p.stops.push(unit.stop);
        dsps.push({ unit, ranges, envs: envs.filter((c, j) => c.kind === 'dsp' && (j === i || c.dsp_parent_envelope === i)) });
      });
      tail.connect(gain);
      const voices = layer.sounds.map(s => {
        const g = ctx.createGain();
        g.gain.value = 0;
        g.connect(vol);
        return { s, gain: g, srcs: [], inside: false, pending: 0, next: Infinity };
      });
      p.layers.push({ layer, vol, pan, dsps, pitch: 0, voices });
    }
    this.playing.set(key, p);
    // An event started in a paused category starts paused.
    if (this.catIsPaused(p.category)) this.setFrozen(p, false, true);
    return p;
  }

  /** Stops the playback of `key` (with its handle `serial` when given: an event for an older
   * handle of a reused instance stops nothing). A fade plays out for `fadeS` seconds (FMOD's
   * linear ramp of the event's group volume, harness/audio fmodprobe.py). */
  private stop(key: string, fadeS: number, serial?: number): void {
    const ctx = this.ctx;
    // `Event::stop(true)` during a fade-out ends it at once (SkipGoLoop in the utility trace).
    const fading = this.fading.get(key);
    if (fading && fadeS === 0 && ctx && (serial === undefined || serial === fading.serial) && !this.playing.has(key)) {
      this.fading.delete(key);
      this.fadeEnd.delete(key);
      for (const L of fading.layers) for (const v of L.voices) for (const x of v.srcs) { try { x.src?.stop(ctx.currentTime); } catch { /* not started */ } }
      return;
    }
    const p = this.playing.get(key);
    if (!p || !ctx || (serial !== undefined && serial !== p.serial)) return;
    this.playing.delete(key);
    // The handle keeps its value; a value already set for the next handle (AutoPlaySound sets
    // its parameters between getEvent, which steals this instance, and start) stays.
    const pending = this.params.get(key);
    if (!pending || pending.serial === p.serial) this.params.set(key, { serial: p.serial, value: p.value });
    p.stopped = true;
    const t = ctx.currentTime;
    if (fadeS > 0) {
      const f = p.fade.gain;
      f.cancelScheduledValues(t);
      f.setValueAtTime(f.value, t);
      f.linearRampToValueAtTime(0, t + fadeS);
      this.fadeEnd.set(key, t + fadeS);
      this.fading.set(key, p);
    }
    const end = p.paused || p.catPaused ? t : t + fadeS;
    for (const L of p.layers) for (const v of L.voices) for (const s of v.srcs) { try { s.src?.stop(end); } catch { /* not started */ } }
    for (const s of p.stops) { try { s(); } catch { /* stopped */ } }
  }

  /** The playback of `key` if it is handle `serial`'s. */
  private current(key: string, serial: number): Playing | null {
    const p = this.playing.get(key);
    return p && p.serial === serial ? p : null;
  }

  /** Freezes or thaws `p` for its event pause and its category's pause: the sources stop where
   * they are and start again from there. */
  private setFrozen(p: Playing, paused: boolean, catPaused: boolean): void {
    const ctx = this.ctx;
    const was = p.paused || p.catPaused, now = paused || catPaused;
    p.paused = paused;
    p.catPaused = catPaused;
    if (!ctx || was === now) return;
    const t = ctx.currentTime;
    for (const L of p.layers) for (const v of L.voices) {
      if (now) {
        for (const s of v.srcs) {
          if (!s.src) continue;
          s.off += Math.max(0, t - s.t0) * s.src.playbackRate.value;
          s.src.onended = null;
          try { s.src.stop(); } catch { /* not started */ }
          s.src.disconnect();
          s.src = null;
        }
        if (v.next !== Infinity) v.next -= t;
      } else {
        v.srcs = v.srcs.filter(s => s.loop || s.off < s.buf.duration);
        for (const s of v.srcs) this.play(ctx, v, s, L.pitch, t);
        if (v.next !== Infinity) v.next += t;
      }
    }
    if (now) p.frozenAt = t;
    else p.last = t;
  }

  /** Starts source entry `s` of voice `v` at its offset at time `when`. */
  private play(ctx: AudioContext, v: Voice, s: Src, pitch: number, when: number): void {
    const src = ctx.createBufferSource();
    src.buffer = s.buf;
    let off = s.off;
    if (s.loop) {
      src.loop = true;
      const [a, b] = s.loop;
      if (b > a) { src.loopStart = a; src.loopEnd = b; if (off > b) off = a + (off - a) % (b - a); }
      else off %= s.buf.duration;
    }
    src.playbackRate.value = 2 ** (s.oct + pitch);
    src.connect(s.g);
    src.onended = () => { v.srcs = v.srcs.filter(x => x !== s); };
    s.src = src;
    s.t0 = Math.max(when, ctx.currentTime);
    s.off = off;
    src.start(s.t0, off);
  }

  /** Applies the sound events of one or more ticks, then runs one FMOD update. */
  handle(events: SoundEvent[]): void {
    if (!this.events.size) return;
    const ctx = this.ctx;
    // Press refusals of teams this machine does not play (the original's requester-only sound).
    const local = this.localTeam;
    const refused = new Set(events.filter(e => e.kind === SOUND_TEAM && local && !local(e.team ?? 0)).map(e => `${e.event}/${e.instance}/${e.serial}`));
    for (const e of events) {
      const key = `${e.event}/${e.instance}`;
      if (e.kind === SOUND_PARAM) this.setParam(key, e);
      if (e.kind === SOUND_AUDIO && e.audio) { this.audioCall(e); continue; }
      if (!ctx) continue;
      const t = ctx.currentTime;
      if (e.kind === SOUND_START) {
        const ev = this.event(e.path);
        if (!ev || refused.has(`${key}/${e.serial}`)) continue;
        this.started++;
        this.start(key, e, ev);
      } else if (e.kind === SOUND_STOP) this.stop(key, 0, e.serial);
      else if (e.kind === SOUND_FADE) this.stop(key, (this.event(e.path)?.properties?.fade_out_ms ?? 300) / 1000, e.serial);
      else if (e.kind === SOUND_PAUSE || e.kind === SOUND_RESUME) {
        const p = this.current(key, e.serial);
        if (p) this.setFrozen(p, e.kind === SOUND_PAUSE, p.catPaused);
      } else if (e.kind === SOUND_POS && e.pos) {
        const p = this.current(key, e.serial);
        if (p) { p.pos = e.pos; this.place(p); }
      } else if (e.kind === SOUND_VOLUME && e.volume !== undefined) {
        const p = this.current(key, e.serial);
        if (p) { p.volume = e.volume; if (!p.muted) p.gain.gain.setValueAtTime(e.volume, t); }
      } else if (e.kind === SOUND_MUTE) {
        // StopAutoPlaySound: setVolume(0) on every instance of the event.
        for (const p of this.playing.values()) {
          if (p.eventIdx !== e.event) continue;
          p.muted = true;
          p.gain.gain.cancelScheduledValues(t);
          p.gain.gain.setValueAtTime(0, t);
        }
      }
    }
    if (ctx) this.update(ctx);
  }

  /** A `SoundResourceMan`/audio-manager call of the sim (SoundEvent.audio). */
  private audioCall(e: SoundEvent): void {
    const c = e.audio;
    if (!c) return;
    switch (c.op) {
      case AUDIO_CATEGORY_VOLUME:
        // TurnLogic's SFX ramp ends at the profile's level (its m_fOriginSFXVolume).
        this.setCategoryVolume(c.a, c.a === CAT_SFX ? Math.min(c.value[0], PROFILE.sfx) : c.value[0]);
        break;
      case AUDIO_CATEGORY_PAUSE:
        this.pauseCategory(c.a, c.b !== 0);
        break;
      case AUDIO_TRACK_LOAD:
        this.trackLoad(c.a, e.path, c.b);
        break;
      case AUDIO_TRACK_QUEUE: {
        // TurnLogic::PostRestoreSnapshot: only when the music main track does not play the music
        // it queues first (the ambient and water queues follow it in the same step).
        if (c.value[1] === 1) {
          if (c.a === TRACK_MUSIC) this.restoreMusic = e.path;
          const want = this.restoreMusic;
          if (want && this.tracks[TRACK_MUSIC][TRACK_MAIN].name.toLowerCase() === want.toLowerCase()) break;
        }
        this.trackQueue(c.a, e.path, c.b);
        break;
      }
      case AUDIO_TRACK_PLAY:
        this.trackPlay(c.a, c.b);
        break;
      case AUDIO_TRACK_FADE_OUT:
        if (c.a === AUDIO_ALL_TYPES) this.trackFadeOutAll(c.b);
        else this.trackFadeOut(c.a, c.b);
        break;
      case AUDIO_TRACK_POSITION:
        this.follow.set(c.a, [c.value[0], c.value[1]]);
        this.trackPosition(c.a, [this.eyeX, c.value[0], c.value[1]]);
        break;
    }
  }

  /** FMOD's `EventSystem::update`: the parameters advance, the sound instances follow the cursor,
   * the envelopes are applied and finished one-shot events go; then `SoundResourceMan`'s tracks
   * (`LogicUpdate`, on the elapsed time). */
  private update(ctx: AudioContext): void {
    const now = ctx.currentTime;
    for (const [key, p] of this.playing) {
      const dt = Math.max(0, now - p.last);
      p.last = now;
      if (p.paused || p.catPaused) continue;
      if (p.param) this.advance(p, p.param, dt);
      this.refresh(ctx, p, now);
      if (this.ended(p)) {
        this.playing.delete(key);
        this.params.set(key, { serial: p.serial, value: p.value });
        for (const s of p.stops) { try { s(); } catch { /* stopped */ } }
      }
    }
    for (const [key, p] of this.fading) if ((this.fadeEnd.get(key) ?? 0) <= now || this.playing.has(key)) { this.fading.delete(key); void p; }
    const dt = Math.max(0, now - this.tracksAt);
    this.tracksAt = now;
    this.tracksUpdate(dt);
  }

  /** Runs FMOD's and the tracks' update without sim events (the front end). */
  tick(): void {
    if (this.ctx && this.events.size) this.update(this.ctx);
  }

  private advance(p: Playing, q: Param, dt: number): void {
    const range = q.max - q.min;
    if (range <= 0) return;
    if (p.target !== null) {
      const step = q.seek_speed * range * dt, d = p.target - p.value;
      if (Math.abs(d) <= step) { p.value = p.target; p.target = null; } else p.value += Math.sign(d) * step;
    } else if (q.velocity) {
      p.value += q.velocity * range * dt;
      if (p.value > q.max) p.value = q.loop_mode === 'oneshot' ? q.max : q.min + (p.value - q.min) % range;
    }
  }

  private refresh(ctx: AudioContext, p: Playing, now: number): void {
    const q = p.param;
    const x = q && q.max > q.min ? Math.min(1, Math.max(0, (p.value - q.min) / (q.max - q.min))) : 0;
    const jump = p.fresh;
    p.fresh = false;
    for (const L of p.layers) {
      let vol = 1, pan = 0, pitch = 0;
      for (const en of L.layer.envelopes ?? []) {
        if (en.kind === 'volume') vol *= envelope(en.points, x);
        else if (en.kind === 'pitch') pitch += 8 * (envelope(en.points, x) - 0.5);
        else if (en.kind === 'pan') pan = 1 - 2 * envelope(en.points, x);
      }
      ramp(L.vol.gain, vol, now, jump);
      ramp(L.pan.pan, Math.max(-1, Math.min(1, pan)), now, jump);
      for (const d of L.dsps) {
        const values = d.ranges.map(([lo]) => lo);
        for (const en of d.envs) {
          const r = d.ranges[en.dsp_param_index];
          if (!r) continue;
          const y = envelope(en.points, x);
          values[en.dsp_param_index] = en.mapping === 1 && r[0] > 0 ? r[0] * (r[1] / r[0]) ** y : r[0] + (r[1] - r[0]) * y;
        }
        d.unit.apply(values, now);
      }
      if (pitch !== L.pitch) {
        L.pitch = pitch;
        // FMOD sets a channel's frequency at its update, unsmoothed.
        for (const v of L.voices) for (const s of v.srcs) s.src?.playbackRate.setValueAtTime(2 ** (s.oct + pitch), now);
      }
      for (const v of L.voices) this.voice(ctx, p, L, v, x, now, jump);
    }
  }

  /** One sound instance at cursor `x`: region, crossfade, start/stop/mute and spawns. */
  private voice(ctx: AudioContext, p: Playing, L: LayerState, v: Voice, x: number, now: number, jump: boolean): void {
    const s = v.s, end = s.start + s.length, eps = 1e-6;
    const inside = x >= s.start - eps && (x < end - eps || (end >= 1 - 1e-4 && x >= end - eps));
    let gain = 0;
    if (inside) {
      gain = s.volume ?? 1;
      const fade = (f: number, type: number) => { const c = Math.min(1, Math.max(0, f)); return type === 6 ? c : Math.sqrt(c); };
      if (s.fade_in > 0 && s.length > 0) { const w = s.fade_in * s.length; if (x < s.start + w) gain *= fade((x - s.start) / w, s.fade_in_type); }
      if (s.fade_out > 0 && s.length > 0) { const w = s.fade_out * s.length; if (x > end - w) gain *= fade((end - x) / w, s.fade_out_type); }
    }
    ramp(v.gain.gain, gain, now, jump || (inside && !v.inside));
    const def = this.sounddefs[s.sounddef], props = def?.properties ?? {};
    const spawns = (props.spawn_time_max_ms ?? 0) > 0;
    if (inside && !v.inside) {
      // Entering: a loop or a finished one-shot starts (a muted one-shot plays on audibly).
      if (spawns) v.next = now;
      else if (!v.srcs.length && !v.pending) this.trigger(ctx, p, L, v, def);
    } else if (!inside && v.inside) {
      // Leaving: a looping sound stops; one-shots play on muted; spawning ends.
      v.next = Infinity;
      if (s.loop_mode !== 'oneshot') {
        for (const x of v.srcs) { try { x.src?.stop(); } catch { /* not started */ } }
        v.srcs = [];
      }
    }
    v.inside = inside;
    if (inside && spawns && now >= v.next) {
      if (v.srcs.length + v.pending < Math.max(1, props.max_spawned ?? 1)) this.trigger(ctx, p, L, v, def);
      v.next = now + rand(props.spawn_time_min_ms ?? 0, props.spawn_time_max_ms ?? 0) / 1000;
    }
  }

  /** Plays one entry of the sound definition on voice `v` after its trigger delay. */
  private trigger(ctx: AudioContext, p: Playing, L: LayerState, v: Voice, def: SoundDef | undefined): void {
    const e = def && this.pick(def);
    if (!def || !e) return;
    const props = def.properties ?? {};
    const delay = rand(props.trigger_delay_min_ms ?? 0, props.trigger_delay_max_ms ?? 0) / 1000;
    const when = ctx.currentTime + delay;
    v.pending++;
    p.triggered = true;
    void this.buffer(e.file).then(buf => {
      v.pending--;
      // Gone, or a looping sound whose region was left meanwhile.
      if (!buf || p.stopped || (v.s.loop_mode !== 'oneshot' && !v.inside)) return;
      const g = ctx.createGain();
      g.gain.value = (props.volume ?? 1) * rand(props.volume_randomization_min_gain ?? 1, 1);
      g.connect(v.gain);
      const oct = p.octaves + (props.pitch_octaves ?? 0) + rand(-1, 1) * (props.pitch_randomization_octaves ?? 0);
      const loop: Src['loop'] = v.s.loop_mode === 'oneshot' ? null
        : e.loop_end > e.loop_start ? [e.loop_start / e.rate, e.loop_end / e.rate] : [0, 0];
      const s: Src = { src: null, oct, buf, g, loop, t0: when, off: 0 };
      v.srcs.push(s);
      if (p.paused || p.catPaused) return; // starts with the thaw
      this.play(ctx, v, s, L.pitch, when);
    });
  }

  /** A one-shot event is over once every sound it started has ended: no source plays or waits,
   * and no looping or spawning sound instance could start again. An event without sounds
   * (SentryGunFire) is over at its first update, as FMOD's (human-sentry-minions trace). */
  private ended(p: Playing): boolean {
    if (!p.triggered && p.layers.some(L => L.voices.length)) return false;
    for (const L of p.layers) {
      for (const v of L.voices) {
        if (v.srcs.length || v.pending || v.s.loop_mode !== 'oneshot') return false;
        if ((this.sounddefs[v.s.sounddef]?.properties?.spawn_time_max_ms ?? 0) > 0) return false;
      }
    }
    return true;
  }

  /** The listener (CameraMan::UpdateGraphicCameras): at the camera's look-at point (`x`, `y`,
   * `z` + 1), facing (`fx`, `fy`, `fz`) with the camera's up (0, 1, 0); every 3D instance is
   * re-placed. `eyeX`: the camera's own x, where the water track sits
   * (`BackgroundMan::UpdateWaterLevel`). */
  listen(x: number, y: number, z: number, fx: number, fy: number, fz: number, eyeX = x): void {
    // right = forward x up, normalised
    const rx = -fz, rz = fx, n = Math.hypot(rx, rz) || 1;
    this.ear = { x, y, z: z + 1, rx: rx / n, ry: 0, rz: rz / n };
    this.eyeX = eyeX;
    for (const [type, [fy2, fz2]] of this.follow) this.trackPosition(type, [eyeX, fy2, fz2]);
    for (const p of this.playing.values()) this.place(p);
  }

  /** FMOD Ex's 3D attenuation (linear: 1 inside `min_distance`, 0 from `max_distance`;
   * logarithmic: min / distance, held from `max_distance`) and stereo pan (the source's
   * direction on the listener's right axis, times `pan_level_3d`). */
  private place(p: Playing): void {
    if (!p.spatial || !p.pos || !this.ctx) return;
    const e = this.ear, dx = p.pos[0] - e.x, dy = p.pos[1] - e.y, dz = p.pos[2] - e.z;
    const d = Math.hypot(dx, dy, dz);
    const min = p.props.min_distance ?? 1, max = Math.max(min, p.props.max_distance ?? 10000);
    const g = d <= min ? 1 : p.props.rolloff === 'linear' ? Math.max(0, (max - d) / (max - min)) : min / Math.min(d, max);
    const pan = d > 0 ? (dx * e.rx + dy * e.ry + dz * e.rz) / d : 0;
    const t = this.ctx.currentTime;
    p.spatial.dist.gain.setValueAtTime(g, t);
    p.spatial.pan.pan.setValueAtTime(Math.max(-1, Math.min(1, pan * (p.props.pan_level_3d ?? 1))), t);
  }

  // ---- the client's own instances -------------------------------------------------------------

  /** `getEvent(path)`: a new handle (null: no such event). */
  localCreate(path: string): LocalSound | null {
    const ev = this.event(path);
    return ev ? { key: `l${++this.localN}`, serial: 0, path: ev.path, ev, volume: null, pos: null } : null;
  }

  /** `Event::start` (a playing handle restarts). */
  localPlay(l: LocalSound): void {
    l.serial = Math.max(1, l.serial);
    const p = this.start(l.key, { kind: SOUND_START, path: l.path, event: -1, instance: -1, serial: l.serial }, l.ev);
    if (!p || !this.ctx) return;
    if (l.volume !== null) { p.volume = l.volume; p.gain.gain.value = l.volume; }
    if (l.pos) { p.pos = l.pos; this.place(p); }
  }

  /** `Event::stop(immediate)`. */
  localStop(l: LocalSound, immediate: boolean): void {
    this.stop(l.key, immediate ? 0 : (l.ev.properties?.fade_out_ms ?? 0) / 1000);
  }

  /** `Event::getState`'s playing bit: playing, or fading out. */
  localIsPlaying(l: LocalSound): boolean {
    const t = this.ctx?.currentTime ?? 0;
    return this.playing.has(l.key) || (this.fadeEnd.get(l.key) ?? -1) > t;
  }

  /** `XSoundInstance::SetVolume`: the event volume of this instance. */
  localSetVolume(l: LocalSound, v: number): void {
    l.volume = v;
    const p = this.playing.get(l.key);
    if (p && this.ctx) { p.volume = v; if (!p.muted) p.gain.gain.setValueAtTime(v, this.ctx.currentTime); }
  }

  /** `XSoundInstance::SetPosition`. */
  localSetPosition(l: LocalSound, pos: [number, number, number]): void {
    l.pos = pos;
    const p = this.playing.get(l.key);
    if (p) { p.pos = pos; this.place(p); }
  }

  /** `SoundMan::PlaySound(name)` from the UI (`AutoPlaySound`, no position): a new handle of the
   * event's pool (`max_playbacks`; the oldest playing one is stolen), started at once. */
  playUi(path: string): void {
    const ev = this.event(path);
    if (!ev) return;
    const list = (this.uiSounds.get(ev.path) ?? []).filter(x => this.localIsPlaying(x));
    const max = Math.max(1, ev.properties?.max_playbacks ?? 1);
    // FMOD's pool: the oldest instance is taken (it stops), as `getEvent` does.
    while (list.length >= max) { const old = list.shift(); if (old) this.stop(old.key, 0); }
    const l = this.localCreate(ev.path);
    if (!l) return;
    // SoundMan::PlaySound without a position: AutoPlaySound's set3DAttributes at the origin.
    l.pos = [0, 0, 0];
    list.push(l);
    this.uiSounds.set(ev.path, list);
    this.started++;
    this.localPlay(l);
  }

  // ---- SoundResourceMan's tracks (SoundResourceMan.cpp) ---------------------------------------

  private static emptyTrack(): Track {
    return { name: '', queued: '', inst: null, origin: 1, current: 1, speed: 1, destroy: false, fadingOut: false, fadingIn: false };
  }

  /** The event the music main track plays (`SoundResourceMan::GetName`). */
  trackName(type: number, track: number): string {
    return this.tracks[type]?.[track]?.name ?? '';
  }

  /** `SoundResourceMan::Load(type, group, name, track)` 0xbc63e0: FadeOut and Unload of the
   * track, then a new instance of `name` (its designed volume as the origin). */
  trackLoad(type: number, name: string, track: number): boolean {
    if (!name || track > 1) return false;
    this.trackFadeOut(type, track);
    this.trackUnload(type, track);
    const t = this.tracks[type][track];
    t.name = name;
    if (!t.inst) {
      const l = this.localCreate(name);
      if (!l) return false;
      t.inst = l;
      t.origin = l.ev.properties?.volume ?? 1;
    }
    return true;
  }

  /** `SoundResourceMan::Unload` 0xbc5fe0: the instance released (`~XSoundInstance` stops it), the
   * track reset. */
  trackUnload(type: number, track: number): void {
    if (track > 1) return;
    const t = this.tracks[type][track];
    if (t.inst && this.localIsPlaying(t.inst)) this.localStop(t.inst, true);
    this.tracks[type][track] = Audio.emptyTrack();
    this.tracks[type][track].queued = t.queued;
  }

  /** `SoundResourceMan::Queue` 0xbc4f90: the next event of the track, after a FadeOut. */
  trackQueue(type: number, name: string, track: number): void {
    if (track > 1) return;
    this.tracks[type][track].queued = name;
    this.trackFadeOut(type, track);
  }

  /** `SoundResourceMan::Play` 0xbc5020. */
  trackPlay(type: number, track: number): void {
    const t = this.tracks[type]?.[track];
    if (t?.inst) this.localPlay(t.inst);
  }

  /** `SoundResourceMan::FadeOut` 0xbc4f30: the instance fades out (`stop(false)`) and goes. */
  trackFadeOut(type: number, track: number): void {
    const t = this.tracks[type]?.[track];
    if (!t?.inst || t.destroy) return;
    this.localStop(t.inst, false);
    t.destroy = true;
  }

  /** `SoundResourceMan::FadeOutAllType(track)` 0xbc5170. */
  trackFadeOutAll(track: number): void {
    for (let type = 0; type < 3; type++) this.trackFadeOut(type, track);
  }

  /** `SoundResourceMan::SetPosition(type, pos)` 0xbc7240: both tracks of the type. */
  trackPosition(type: number, pos: [number, number, number]): void {
    for (const t of this.tracks[type] ?? []) if (t.inst) this.localSetPosition(t.inst, pos);
  }

  /** `SoundResourceMan::LogicUpdate` 0xbc65a0 over `dt` seconds (`kLogicUpdate * speed` per
   * tick): a fading-out track goes once its instance stopped and its queued event loads and
   * plays; volume fades in and out (`FadeIn`, `TempFadeOut`) move by `speed` per second. */
  private tracksUpdate(dt: number): void {
    for (let type = 0; type < 3; type++) {
      for (let track = 0; track < 2; track++) {
        const t = this.tracks[type][track];
        if (t.destroy && t.inst && !this.localIsPlaying(t.inst)) this.trackUnload(type, track);
        const u = this.tracks[type][track];
        if (!u.inst && u.queued) {
          const q = u.queued;
          this.trackLoad(type, q, track);
          this.trackPlay(type, track);
          this.tracks[type][track].queued = '';
        }
        const w = this.tracks[type][track];
        if (!w.inst || w.destroy || !this.localIsPlaying(w.inst)) continue;
        if (!w.fadingOut || w.current === 0) {
          if (w.fadingIn && w.current !== w.origin) {
            w.current = Math.min(w.origin, w.current + dt * w.speed);
            this.localSetVolume(w.inst, w.current);
            if (w.current === w.origin) w.fadingIn = false;
          }
        } else {
          w.current = Math.max(0, w.current - dt * w.speed);
          this.localSetVolume(w.inst, w.current);
          if (w.current === 0) w.fadingOut = false;
        }
      }
    }
  }

  // ---- the front end's and the results' music (UI callbacks) ----------------------------------

  /** `UICBInterface_ALL_SCENE::ChangeBackgroundMusic` 0xeec910 (Home's OnCreate, and coming back
   * from a match): the theme's `FrontendMusic` and `FrontendAmbient` load and play on the music
   * and ambient main tracks. */
  frontEnd(music: string | null, ambience: string | null): void {
    if (!music || !ambience) return;
    this.trackLoad(TRACK_MUSIC, music, TRACK_MAIN);
    this.trackPlay(TRACK_MUSIC, TRACK_MAIN);
    this.trackLoad(TRACK_AMBIENT, ambience, TRACK_MAIN);
    this.trackPlay(TRACK_AMBIENT, TRACK_MAIN);
    this.trackPlay(TRACK_WATER, TRACK_MAIN);
  }

  /** The front end's scenes' OnDestroy (Home, the lobbies, the loading screens):
   * `FadeOutAllType(kMainTrack)`. */
  leaveFrontEnd(): void {
    this.trackFadeOutAll(TRACK_MAIN);
  }

  /** `UICallback_RoundResults_OnCreate` 0xf0f570: the theme's tracks fade out
   * (`ThemeMan::UnloadResources(0xc)`, `FadeOutAllType(kMainTrack)`), the results music plays
   * on the music sub track and the parade's "Worms/RoundResults/SFX/Parade_%02u" (`parade`: the
   * parade mesh's number). */
  resultsMusic(parade: number): void {
    this.trackFadeOutAll(TRACK_MAIN);
    this.trackLoad(TRACK_MUSIC, 'Worms/Frontend_Music/RoundResults', TRACK_SUB);
    this.trackPlay(TRACK_MUSIC, TRACK_SUB);
    this.resultsSounds = [`Worms/RoundResults/SFX/Parade_${String(parade).padStart(2, '0')}`];
    this.playUi(this.resultsSounds[0]);
  }

  /** `UpdateFanfare` 0xf0ea40 (game/uisounds.ts `fanfareSounds`): the fanfare, then the crowd. */
  resultsFanfare(fanfare: string | null, crowd: string | null): void {
    for (const path of [fanfare, crowd]) {
      if (!path) continue;
      this.resultsSounds.push(path);
      this.playUi(path);
    }
  }

  /** `FadeoutResultsMusic` 0xf0eee0 (the results' exit; OnDestroy's `FadeOut(kMusic, kSubTrack)`
   * finds nothing then): the results music stops at once (`Unload(kMusic, kSubTrack)`), the
   * crowd, the parade and the fanfare are muted (`StopAutoPlaySound`; they play on). */
  resultsMusicEnd(): void {
    this.trackUnload(TRACK_MUSIC, TRACK_SUB);
    for (const path of this.resultsSounds) this.stopAutoPlay(path);
    this.resultsSounds = [];
  }

  /** `XAudioManager::StopAutoPlaySound(name)` 0x11c6d20 for the UI's sounds: `Event::getInfo`'s
   * instances of the event get `setVolume(0)` (a playing one plays on muted; the results' trace
   * shows the call for instances that have ended, too). */
  stopAutoPlay(path: string): void {
    const ev = this.event(path);
    for (const l of (ev && this.uiSounds.get(ev.path)) ?? []) this.localSetVolume(l, 0);
  }
}

/** The client's one audio system: the front end and the matches share it (music runs across). */
export const audio = new Audio();
